import React from 'react';

import { cssMap } from '@atlaskit/css';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import { ButtonItem } from '@atlaskit/menu';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { ReasoningModeMenuItem as ReasoningModeMenuItemType } from './types';

const styles = cssMap({
	roundedItem: {
		borderRadius: token('radius.medium'),
		overflow: 'hidden',
	},
	checkmark: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	iconContainerDefault: {
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: token('radius.full'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		color: token('color.text'),
	},
	iconContainerSelected: {
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: token('radius.full'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: 'transparent',
		backgroundColor: token('color.background.neutral.bold'),
		color: token('color.text.inverse'),
	},
});

type Props = ReasoningModeMenuItemType;

export const ReasoningModeMenuItemComponent = ({
	id,
	title,
	subtitle,
	icon,
	isSelected,
	isDisabled = false,
	lozengeLabel,
	testId,
	onSelect,
	wrapper: Wrapper,
}: Props): React.JSX.Element => {
	const content = (
		<Box xcss={styles.roundedItem}>
			<ButtonItem
				role="menuitemradio"
				aria-checked={isSelected}
				isDisabled={isDisabled}
				onClick={onSelect}
				iconBefore={
					<Box xcss={isSelected ? styles.iconContainerSelected : styles.iconContainerDefault}>
						{icon}
					</Box>
				}
				testId={testId ?? `reasoning-mode-menu-item-${id}`}
			>
				<Inline alignBlock="center" spread="space-between" space="space.200">
					<Stack space="space.050">
						<Inline alignBlock="center" space="space.100">
							<Text size="medium" weight="medium">
								{title}
							</Text>
							{lozengeLabel}
						</Inline>
						{subtitle && (
							<Text size="small" weight="regular" color="color.text.subtle">
								{subtitle}
							</Text>
						)}
					</Stack>
					{isSelected && (
						<Box xcss={styles.checkmark}>
							<CheckMarkIcon label="" />
						</Box>
					)}
				</Inline>
			</ButtonItem>
		</Box>
	);

	if (Wrapper) {
		return <Wrapper isSelected={isSelected}>{content}</Wrapper>;
	}

	return content;
};
